"use client";

import React from "react";
import { ScanBarcode, Printer, Tablet, Wifi, Box, Settings, CircleCheck, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const devices = [{
  id: 1,
  title: "Scanner RF Industriali",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/industrial_rf_scanner_device.png",
  desc: "Terminali portatili ultra-resistenti per gestione intensiva di magazzino."
}, {
  id: 2,
  title: "Stampanti Termiche",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/thermal_label_printer_device.png",
  desc: "Sistemi di stampa etichette ad alto volume per logistica e spedizioni."
}, {
  id: 3,
  title: "Tablet Rugged",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/rugged_tablet_device.png",
  desc: "Dispositivi mobili rinforzati per carrellisti e operatori sul campo."
}, {
  id: 4,
  title: "Industrial Scale",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/industrial_scale.png",
  desc: "Bilancia industriale per pesatura precisa di carichi pesanti."
}];
const specs = [{
  category: "Terminali RF",
  os: "Android Enterprise",
  durability: "IP65 / IP67, Drop 2.4m",
  connectivity: "Wi-Fi 6, 5G, NFC",
  use_case: "Picking, Inventario, Ricevimento Merce"
}, {
  category: "Stampanti Etichette",
  os: "Link-OS / Proprietario",
  durability: "Industrial Metal Frame",
  connectivity: "Ethernet, Wi-Fi, BT",
  use_case: "Etichettatura Pallet, Spedizioni, Asset"
}, {
  category: "Sistemi di Pesatura Industriale",
  os: "Windows 10/11 IoT, Android con Embedded / Ethernet / Serial Integration",
  durability: "MIL-STD-810G, Vibration Proof con Industrial Steel Frame, IP65",
  connectivity: "4G/5G, GPS, CanBus",
  use_case: "Carrelli Elevatori, Gestione Flotta con Pesatura pallet, controllo peso, integrazione WMS."
}, {
  category: "Scanner Wearable",
  os: "Proprietario / Android Wear",
  durability: "IP65, Tumble Spec",
  connectivity: "Bluetooth 5.0, NFC",
  use_case: "Picking Intensivo, Sorting"
}, {
  category: "Infrastruttura Wi-Fi",
  os: "Controller-less / Cloud",
  durability: "Indoor / Outdoor IP67",
  connectivity: "Wi-Fi 6E, Mesh",
  use_case: "Copertura Magazzini, Celle Frigo"
}];
export default function LogisticaEDispositivi() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="logistica-e-dispositivi" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-2 text-primary mb-4">
            <Box className="w-5 h-5" />
            <span className="text-sm font-mono uppercase tracking-wider font-semibold">
              Hardware & Operations
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-white">
            Logistica e Dispositivi <span className="text-primary">Rugged</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Supporto specializzato per tecnologie operative in ambienti critici. 
            Dalla configurazione di scanner RF alla gestione di parchi dispositivi complessi, 
            garantiamo continuità operativa dove l'hardware consumer non basta.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-card/50 border border-border/50 rounded-xl p-6 backdrop-blur-sm shadow-2xl">
              <div className="flex items-center justify-between mb-6 border-b border-border/50 pb-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  <ScanBarcode className="w-5 h-5 text-primary" />
                  Galleria Dispositivi
                </h3>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500/50" />
                  <div className="w-2 h-2 rounded-full bg-yellow-500/50" />
                  <div className="w-2 h-2 rounded-full bg-green-500/50" />
                </div>
              </div>
              
              <Carousel className="w-full">
                <CarouselContent>
                  {devices.map((device, index) => <CarouselItem key={device.id} data-index={index}>
                      <div className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-muted/30">
                        <img src={device.image} alt={device.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end p-6">
                          <h4 className="text-white font-bold text-xl mb-1">{device.title}</h4>
                          <p className="text-gray-300 text-sm">{device.desc}</p>
                        </div>
                      </div>
                    </CarouselItem>)}
                </CarouselContent>
                <div className="flex justify-end gap-2 mt-4">
                  <CarouselPrevious className="static translate-y-0 translate-x-0 hover:bg-primary hover:text-primary-foreground border-border" />
                  <CarouselNext className="static translate-y-0 translate-x-0 hover:bg-primary hover:text-primary-foreground border-border" />
                </div>
              </Carousel>
            </div>

            {}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card/30 border border-border/50 p-4 rounded-lg">
                <Wifi className="w-6 h-6 text-primary mb-2" />
                <h4 className="font-semibold text-white">Connettività</h4>
                <p className="text-xs text-muted-foreground">Wi-Fi 6 & 5G Ready</p>
              </div>
              <div className="bg-card/30 border border-border/50 p-4 rounded-lg">
                <Settings className="w-6 h-6 text-primary mb-2" />
                <h4 className="font-semibold text-white">Configurazione</h4>
                <p className="text-xs text-muted-foreground">Staging, Provisioning &amp; MDM</p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card/20 border border-border rounded-xl overflow-hidden backdrop-blur-md">
              <div className="p-6 border-b border-border bg-card/40 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                    <Tablet className="w-5 h-5 text-primary" />
                    Specifiche Tecniche
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Dettagli operativi per categorie di dispositivi supportati.
                  </p>
                </div>
                <Button variant="outline" size="sm" className="border-primary/50 text-primary hover:bg-primary/10" asChild>
                  <Link to="/ContactsPage">
                    Richiedi Scheda <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>

              <div className="p-0">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow className="hover:bg-transparent border-border">
                      <TableHead className="text-primary font-bold w-[180px]">Categoria</TableHead>
                      <TableHead className="text-muted-foreground">OS / Piattaforma</TableHead>
                      <TableHead className="text-muted-foreground hidden md:table-cell">Durabilità</TableHead>
                      <TableHead className="text-muted-foreground">Utilizzo Tipico</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {specs.map((spec, i) => <TableRow key={i} data-index={i} className="border-border hover:bg-muted/30 transition-colors">
                        <TableCell className="font-medium text-white">
                          <div className="flex items-center gap-2">
                            <CircleCheck className="w-3 h-3 text-primary shrink-0" />
                            {spec.category}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-gray-300">{spec.os}</TableCell>
                        <TableCell className="text-sm text-gray-400 hidden md:table-cell">{spec.durability}</TableCell>
                        <TableCell className="text-sm text-gray-300">{spec.use_case}</TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </div>
              
              <div className="p-4 bg-muted/20 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
                <span>* Configurazioni personalizzabili su richiesta</span>
                <span className="font-mono">REV. 2025.1</span>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                  <Printer className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Gestione Stampa</h4>
                  <p className="text-sm text-muted-foreground">
                    Supporto completo per linguaggi ZPL/EPL e integrazione con WMS/ERP aziendali.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                  <ScanBarcode className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Long Range Scanning</h4>
                  <p className="text-sm text-muted-foreground">
                    Tecnologie di lettura avanzate per codici a barre danneggiati o distanti fino a 15m.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}